import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Pencil, Trash2, Search, Shield, User, RefreshCw, Check } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserManagement() {
  const { getAuthHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'staff' });
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/users`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      toast.error('Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filteredUsers = users.filter(user => 
    user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCreateDialog = () => {
    setSelectedUser(null);
    setFormData({ name: '', email: '', password: '', role: 'staff' });
    setIsDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (selectedUser) {
        const updateData = { ...formData };
        if (!updateData.password) delete updateData.password;
        await axios.put(`${API_URL}/api/users/${selectedUser.id}`, updateData, getAuthHeader());
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${API_URL}/api/users`, formData, getAuthHeader());
        toast.success('Kullanıcı oluşturuldu');
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API_URL}/api/users/${selectedUser.id}`, getAuthHeader());
      toast.success('Kullanıcı silindi');
      setIsDeleteDialogOpen(false);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Silme başarısız');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-[60vh]" data-testid="users-loading"><div className="h-8 w-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold font-[Manrope] tracking-tight">Kullanıcı Yönetimi</h2>
          <p className="text-muted-foreground">Ekip üyelerini ve rollerini yönetin</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={fetchUsers} data-testid="refresh-users"><RefreshCw className="h-4 w-4 mr-2" />Yenile</Button>
          <Button onClick={openCreateDialog} data-testid="add-user-btn"><Plus className="h-4 w-4 mr-2" />Kullanıcı Ekle</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="total-users-stat">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center"><Users className="h-6 w-6 text-primary" /></div>
              <div><p className="text-sm text-muted-foreground">Toplam Kullanıcı</p><p className="text-2xl font-bold font-[Manrope]">{users.length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="admin-count-stat">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center"><Shield className="h-6 w-6 text-amber-500" /></div>
              <div><p className="text-sm text-muted-foreground">Yönetici</p><p className="text-2xl font-bold font-[Manrope]">{users.filter(u => u.role === 'admin').length}</p></div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="staff-count-stat">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-green-500/10 flex items-center justify-center"><User className="h-6 w-6 text-green-500" /></div>
              <div><p className="text-sm text-muted-foreground">Personel</p><p className="text-2xl font-bold font-[Manrope]">{users.filter(u => u.role === 'staff').length}</p></div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div><CardTitle className="font-[Manrope]">Ekip Üyeleri</CardTitle><CardDescription>Kayıtlı tüm kullanıcılar</CardDescription></div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input placeholder="Kullanıcı ara..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-users" />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>Ad</TableHead>
                  <TableHead>E-posta</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead>Oluşturulma</TableHead>
                  <TableHead className="text-right">İşlemler</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">Kullanıcı bulunamadı</TableCell></TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                            <span className="text-xs font-semibold text-primary">{user.name.split(' ').map(n => n[0]).join('').toUpperCase()}</span>
                          </div>
                          {user.name}
                          {user.id === currentUser?.id && <Badge variant="outline" className="text-xs">Siz</Badge>}
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                          {user.role === 'admin' ? <><Shield className="h-3 w-3 mr-1" />Yönetici</> : <><User className="h-3 w-3 mr-1" />Personel</>}
                        </Badge>
                      </TableCell>
                      <TableCell><Badge variant="default" className="bg-green-500/10 text-green-600 hover:bg-green-500/20">{user.is_active ? 'Aktif' : 'Pasif'}</Badge></TableCell>
                      <TableCell className="text-muted-foreground">{new Date(user.created_at).toLocaleDateString('tr-TR')}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="icon" onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}><Pencil className="h-4 w-4" /></Button>
                          {user.id !== currentUser?.id && (
                            <Button variant="ghost" size="icon" onClick={() => { setSelectedUser(user); setIsDeleteDialogOpen(true); }} className="text-destructive hover:text-destructive" data-testid={`delete-user-${user.id}`}><Trash2 className="h-4 w-4" /></Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="user-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">{selectedUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı'}</DialogTitle>
            <DialogDescription>{selectedUser ? 'Kullanıcı bilgilerini güncelleyin' : 'Kullanıcının Google hesap e-postasını girin. Kullanıcı bu e-posta ile Google giriş yapabilecek.'}</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2"><Label htmlFor="name">Ad Soyad</Label><Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Ahmet Yılmaz" required data-testid="user-name-input" /></div>
              <div className="space-y-2"><Label htmlFor="email">E-posta (Google hesabı)</Label><Input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="ahmet@example.com" required data-testid="user-email-input" /></div>
              <div className="space-y-2"><Label htmlFor="role">Rol</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue placeholder="Rol seçin" /></SelectTrigger>
                  <SelectContent><SelectItem value="staff">Personel</SelectItem><SelectItem value="admin">Yönetici</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>İptal</Button>
              <Button type="submit" disabled={saving} data-testid="save-user-btn">{saving ? 'Kaydediliyor...' : <><Check className="h-4 w-4 mr-2" />{selectedUser ? 'Güncelle' : 'Oluştur'}</>}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-user-dialog">
          <AlertDialogHeader><AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle><AlertDialogDescription>{selectedUser?.name} kullanıcısını silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>İptal</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">Sil</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
